import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { fetchCustomers, fetchProperties, submitContract } from '../../utils/api/api';
import toast from 'react-hot-toast';

const AddContractForm = ({ onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        type: '',
        customer_id: '',
        property_id: '',
        status: '',
        start_date: '',
        end_date: '',
    });

    useEffect(() => {
        const getCustomers = async () => {
            try {
                const data = await fetchCustomers({ page: 0, pageSize: 0 });
                setCustomers(data.customers);
            } catch (err) {
                toast.error("Failed to fetch customers.");
            }
        };

        const fetchProjects = async () => {
            try {
                const data = await fetchProperties({
                    page: 1,
                    pageSize: 700,
                });
                setProjects(data.properties);
            } catch (err) {
                toast.error("Failed to fetch projects.");
            }
        };

        getCustomers();
        fetchProjects();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        setFormData(prevState => ({
            ...prevState,
            customer_id: selectedOption ? selectedOption.value : ''
        }));
    };

    const handleProjectSelect = (selectedOption) => {
        setSelectedProject(selectedOption);
        setFormData(prevState => ({
            ...prevState,
            property_id: selectedOption ? selectedOption.value : ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContract(formData);
            onClose();
            toast.success("Contract created successfully.");
            window.location.reload()
        } catch (error) {
            toast.error("Request failed.");
        } finally {
            setLoading(false);
        }
    };

    const customerOptions = customers.map(customer => ({
        value: customer.id,
        label: `${customer.first_name} ${customer.last_name}`
    }));

    const projectOptions = projects.map(project => ({
        value: project.id,
        label: project.title_number
    }));

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {currentStep === 1 && (
                <>
                    <div>
                        <label htmlFor="title" className="block text-black text-[14px] font-medium mb-1">Title</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            placeholder='Enter contract title'
                            value={formData.title}
                            onChange={handleChange}
                            className="border bg-transparent w-full border-gray-200 text-gray-600 font-light text-[12px] rounded-md p-2 outline-none focus:border-secondary"
                        />
                    </div>

                    <div>
                        <label htmlFor="type" className="block text-black text-[14px] font-medium mb-1">Type</label>
                        <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className="border bg-transparent w-full border-gray-200 text-gray-600 font-light text-[12px] rounded-md p-2 outline-none focus:border-secondary"
                        >
                            <option value="">Select a type</option>
                            <option value="offer_letter">Offer Letter</option>
                            <option value="sales_agreement">Sales Agreement</option>
                            <option value="completion_letter">Completion Letter</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="customer_id" className="block text-black text-[14px] font-medium mb-1">Customer</label>
                        <Select
                            value={selectedCustomer}
                            onChange={handleCustomerSelect}
                            options={customerOptions}
                            placeholder="Select customer"
                            isSearchable
                            className="w-full text-[12px] text-black"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="property_id" className="block text-black text-[14px] font-medium mb-1">Property</label>
                        <Select
                            value={selectedProject}
                            onChange={handleProjectSelect}
                            options={projectOptions}
                            placeholder="Select property"
                            isSearchable
                            className="w-full text-[12px] text-black"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="status" className="block text-black text-[14px] font-medium mb-1">Status</label>
                        <select
                            id="status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="border bg-transparent w-full border-gray-200 text-gray-600 font-light text-[12px] rounded-md p-2 outline-none focus:border-secondary"
                        >
                            <option value="">Select a status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="bg-secondary text-white py-1.5 px-6 rounded-md text-[14px] font-medium"
                            onClick={() => setCurrentStep(2)}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {currentStep === 2 && (
                <>
                    <div className="flex w-full items-center gap-4">
                        <div className='w-full'>
                            <label htmlFor="start_date" className="block text-black text-[14px] font-medium mb-1">Start Date</label>
                            <input
                                type="date"
                                id="start_date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="border bg-transparent w-full border-gray-200 text-gray-600 font-light text-[12px] rounded-md p-2 outline-none focus:border-secondary"
                            />
                        </div>
                        <div className='w-full'>
                            <label htmlFor="end_date" className="block text-black text-[14px] font-medium mb-1">End Date</label>
                            <input
                                type="date"
                                id="end_date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleChange}
                                className="border bg-transparent w-full border-gray-200 text-gray-600 font-light text-[12px] rounded-md p-2 outline-none focus:border-secondary"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            className="text-[14px] text-secondary font-medium"
                            onClick={() => setCurrentStep(1)}
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-secondary text-white py-1.5 px-6 rounded-md text-[14px] font-medium"
                        >
                            {loading ? 'Saving...' : 'Save Contract'}
                        </button>
                    </div>
                </>
            )}
        </form>
    );
};

export default AddContractForm;
